import { useJamClient } from '@/src/hooks/useJamClient';
import { useFlashbars } from '@/src/store/flashbar.context';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { useTranslation } from 'react-i18next';
import { Nullable } from '@/src/types/common';
import { IdentityProvider } from '@/src/types/IdentityProvider';
import React, { useContext, useState } from 'react';
import { useAsyncEffect } from '../hooks/useAsyncEffect';
import { User } from '../types/User';
import { preProdLogger } from '../utils/log.utils';
import { useAuth } from './auth.context';
import { AuthRole } from '../constants/shared/auth-roles';

export interface UserContextValue {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>> | null; // Exported for use by role spoofing component
  onNicknameChange: (nickname: string) => void;
  isLoggedIn: boolean;
  loading: boolean;
}

// eslint-disable-next-line no-shadow
export enum UserPreferenceKeys {
  PAGE_SIZE = 'userPreferencesPageSize',
  DISPLAY_TIME = 'userPreferencesSecondaryDisplayTime',
}

export const UserContext = React.createContext<UserContextValue>({
  user: null,
  setUser: null,
  isLoggedIn: false,
  loading: true,
  onNicknameChange: () => {
    // do nothing
  },
});

/**
 * Properties used to create a User.
 */
export interface DynamoDBUser {
  readonly name: string;
  readonly email: string;
  readonly vibeId: string;
  readonly providerName: Nullable<IdentityProvider>;
  readonly login: string;
  readonly roles: string[];
  readonly legacyLogin?: string;
}

const UserProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { authClient } = useAuth();
  const jamClient = useJamClient();
  const { addApiErrorFlashbar } = useFlashbars();
  const { t } = useTranslation();

  // load the user one time
  useAsyncEffect(async () => {
    const idToken = await authClient.getIdToken();

    if (!idToken) {
      setLoading(false);
      return;
    }

    const userFromToken = User.fromIdToken(idToken);

    // call get user API
    try {
      const vibeIdRes = await jamClient.getUserByVibeId({ vibeId: userFromToken.vibeId });

      if (vibeIdRes instanceof Error) {
        addApiErrorFlashbar(vibeIdRes, t(i18nKeys.errors.requestFailed.user.fetch));
        throw vibeIdRes;
      }

      const roles = [...(vibeIdRes.roles || []), AuthRole.GUEST_USER];

      const dbUser = new User({
        name: userFromToken.name,
        email: userFromToken.email,
        vibeId: userFromToken.vibeId,
        identityProvider: userFromToken.provider,
        groups: roles,
        legacyLogin: userFromToken.legacyLogin,
        nickname: vibeIdRes.nickname,
        preferredLanguageCode: vibeIdRes.preferredLanguageCode,
      });

      setUser(dbUser);
      setIsLoggedIn(true);
    } catch (err) {
      preProdLogger(err);
    } finally {
      setLoading(false);
    }
  });

  const onNicknameChange = (nickname: string) => {
    setUser((current) => current?.clone({ nickname }) ?? null);
  };
  const data: UserContextValue = { user, setUser, isLoggedIn, loading, onNicknameChange };

  return <UserContext.Provider value={data}>{children}</UserContext.Provider>;
};

const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser can only be used inside UserProvider');
  }
  return context;
};

export { UserProvider, useUser };
